// src/api/api.js
import axios from 'axios'
import { toast } from 'react-toastify'
import { currentGeneration, endSession, getAccessToken, getRefreshToken, isCurrentGeneration, secondsUntilExpiry, storeRefreshedTokens } from '../auth/session'
import { classifyTicketFailure } from '../services/ws/wsPolicy'

export const API_BASE_URL = 'https://api.yum-express.com/api'

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true
})

// Public endpoints: do NOT send Authorization header
const PUBLIC_ENDPOINTS = [
  /^\/orders\/grocery\/home\/?$/,
  /^\/orders\/grocery\/search\/?(\?.*)?$/,
  /^\/orders\/search\/?(\?.*)?$/,
  /^\/orders\/products\/trending\/?$/,
  /^\/promotions\/home\/?$/,
  /^\/promotions\/flash-sales\/?$/,
  /^\/promotions\/banners\/?/,
  /^\/auth\/vendors(?:\?.*)?$/,
  /^\/orders\/grocery\/category\/\d+\/products\/?$/,
  /^\/auth\/register-with-otp\/?$/,
  /^\/auth\/login\/?$/,
  /^\/auth\/social\/google\/?$/,
  /^\/auth\/social\/apple\/?$/
]

// Calls that establish or renew a session: a 401 from these is an answer, not an expired token.
const AUTH_ENDPOINTS = [/^\/auth\/login\/?$/, /^\/auth\/token\/refresh\/?$/, /^\/auth\/register-with-otp\/?$/, /^\/auth\/verify-otp\/?$/, /^\/auth\/resend-otp\/?$/, /^\/auth\/social\//, /^\/auth\/logout\/?$/]

const isPublicEndpoint = url => PUBLIC_ENDPOINTS.some(rx => rx.test(url || ''))
const isAuthEndpoint = url => AUTH_ENDPOINTS.some(rx => rx.test(url || ''))

// --- Session refresh -------------------------------------------------------
//
// One refresh at a time for the whole page (and, where the browser supports
// Web Locks, for every tab): refresh tokens rotate, so two tabs spending the
// same one would sign one of them out. Only the server refusing the refresh
// token ends the session — being offline, a timeout, 429 or 5xx keep the
// customer signed in and the request fails as what it is.

const REFRESH_LEAD_SECONDS = 60
let inflightRefresh = null

const withTabLock = fn => {
  if (typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function') {
    return navigator.locks.request('yum-express-token-refresh', fn)
  }
  return fn()
}

export function refreshSession() {
  if (inflightRefresh) return inflightRefresh
  const startedIn = currentGeneration()
  const spent = getRefreshToken()
  if (!spent) return Promise.resolve({ kind: 'none' })

  inflightRefresh = withTabLock(async () => {
    // Another tab may have refreshed while we waited for the lock.
    const now = getRefreshToken()
    if (now && now !== spent) return { kind: 'ok', access: getAccessToken() }
    if (!now) return { kind: 'none' }
    try {
      const { data } = await axios.post(`${API_BASE_URL}/auth/token/refresh`, { refresh: spent }, { headers: { 'Content-Type': 'application/json' } })
      if (!isCurrentGeneration(startedIn)) return { kind: 'stale' }
      storeRefreshedTokens({ access: data?.access, refresh: data?.refresh }, startedIn)
      return { kind: 'ok', access: data?.access }
    } catch (e) {
      const status = e?.response?.status
      if (status === 400 || status === 401) {
        // Rejected — unless another tab already rotated it and stored the successor.
        const latest = getRefreshToken()
        if (latest && latest !== spent) return { kind: 'ok', access: getAccessToken() }
        if (endSession('expired', startedIn)) notifySessionExpired()
        return { kind: 'invalid' }
      }
      return { kind: 'unavailable' }
    }
  }).finally(() => {
    inflightRefresh = null
  })
  return inflightRefresh
}

const notifySessionExpired = () => {
  toast.info('Your session has expired. Please sign in again.', { toastId: TOAST_IDS.SESSION })
}

// Request interceptor: attach the current access token, renewing it first when it is about to expire.
api.interceptors.request.use(
  async config => {
    const url = config.url || ''
    config._generation = currentGeneration()
    if (isPublicEndpoint(url)) {
      if (config.headers?.Authorization) delete config.headers.Authorization
      return config
    }
    let token = getAccessToken()
    if (token && !isAuthEndpoint(url) && getRefreshToken()) {
      const left = secondsUntilExpiry(token)
      if (left !== null && left < REFRESH_LEAD_SECONDS) {
        const outcome = await refreshSession()
        if (outcome.kind === 'ok' && outcome.access) token = outcome.access
      }
    }
    if (token && !config.headers?.Authorization) {
      config.headers.Authorization = `Bearer ${token}`
    }
    config._sentToken = token
    return config
  },
  error => Promise.reject(error)
)

// Toast IDs to prevent duplicate stacked error messages
const TOAST_IDS = {
  NETWORK: 'api-network-error',
  SESSION: 'api-session-expired',
  FORBIDDEN: 'api-forbidden-error',
  SERVER: 'api-server-error'
}

// Screens that explain their own failures (checkout, forms) pass
// `suppressErrorToast: true`; payment-provider answers always carry a message
// written for the customer, so the generic "Server error" never covers them.
const screenHandlesIt = error => error.config?.suppressErrorToast || /^PAYMENT_PROVIDER_/.test(error.response?.data?.code || '')

api.interceptors.response.use(
  response => response,
  async error => {
    if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
      return Promise.reject(error)
    }
    const config = error.config || {}
    const url = config.url || ''
    const status = error.response?.status

    // Expired access token: renew once and replay.
    if (status === 401 && !config._retried && !isAuthEndpoint(url) && config._sentToken) {
      config._retried = true
      const current = getAccessToken()
      // The session moved on while this request was out (refresh or new sign-in): replay with today's token.
      if (current && current !== config._sentToken) {
        config.headers.Authorization = `Bearer ${current}`
        config._sentToken = current
        return api(config)
      }
      const outcome = await refreshSession()
      if (outcome.kind === 'ok' && outcome.access) {
        config.headers.Authorization = `Bearer ${outcome.access}`
        config._sentToken = outcome.access
        return api(config)
      }
      if (outcome.kind === 'none' && isCurrentGeneration(config._generation)) {
        // A session from before refresh tokens (or one whose refresh token is gone) cannot be renewed.
        if (endSession('expired', config._generation)) notifySessionExpired()
      }
      return Promise.reject(error)
    }

    if (screenHandlesIt(error)) return Promise.reject(error)

    if (!error.response) {
      toast.info('Network error. Please check your internet connection.', { toastId: TOAST_IDS.NETWORK })
    } else if (status === 403) {
      toast.error('Forbidden. You do not have access.', { toastId: TOAST_IDS.FORBIDDEN })
    } else if (status >= 500) {
      toast.error('Server error. Please try again later.', { toastId: TOAST_IDS.SERVER })
    }
    return Promise.reject(error)
  }
)

/**
 * A single-use WebSocket ticket (POST /auth/ws-ticket), as a TicketResult
 * (services/ws/wsPolicy.js): `{ kind: 'ok', ticket }`, `rate_limited` with the
 * server's Retry-After, `auth` (signed out — the interceptor above already
 * tried one refresh) or `transient`. The socket connects with `?ticket=` —
 * never with the JWT in the URL, which nginx would log.
 */
export async function requestWebSocketTicket() {
  if (!getAccessToken()) return { kind: 'auth' }
  try {
    const { data } = await api.post('/auth/ws-ticket', null, { suppressErrorToast: true })
    return typeof data?.ticket === 'string' && data.ticket ? { kind: 'ok', ticket: data.ticket } : { kind: 'transient' }
  } catch (error) {
    return classifyTicketFailure(error?.response?.status, error?.response?.headers, Date.now())
  }
}

/** The ticket alone, or null when none could be issued. */
export async function getWebSocketTicket() {
  const result = await requestWebSocketTicket()
  return result.kind === 'ok' ? result.ticket : null
}

export default api
